import React, { useState } from 'react';
import { doc, updateDoc, arrayRemove, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import ImageUploadModal from './ImageUploadModal';
import TierUpgradeModal from '../TierUpgrade/TierUpgradeModal';
import ProfileImageSelector from './ProfileImageSelector';
import { isProfileImage } from '../../utils/profileImageUtils';
import { getSubscriptionRanking } from '../../utils/subscriptionRankingUtils';
import { generateDuplicateReport, cleanupDuplicateImages } from '../../utils/duplicateImageUtils';
import { cleanupDuplicatesForUser, checkDuplicatesForUser } from '../../utils/duplicateCleanupUtils';
import { cleanupDatabaseDuplicates } from '../../utils/databaseCleanupUtils';
import { createImagePlaceholder, createPendingPlaceholder, createRejectedPlaceholder } from '../../utils/placeholderImageUtils';

const ImageGallery = ({ 
  images = [], 
  pendingImages = [], 
  rejectedImages = [], 
  girlId, 
  maxImages, 
  isOwner = false,
  onImageUpdate,
  currentTier = 'free',
  userRole = 'girl',
  girlData = null
}) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showTierUpgrade, setShowTierUpgrade] = useState(false);
  const [activeImageTab, setActiveImageTab] = useState('all'); // all, approved, pending, rejected
  const [duplicateCount, setDuplicateCount] = useState(0);

  // Debug: ตรวจสอบ props ที่ได้รับ
  console.log('ImageGallery Debug');
  console.log('- currentTier prop:', currentTier);
  console.log('- userRole prop:', userRole);
  console.log('- maxImages prop:', maxImages);
  console.log('- girlData prop:', girlData);

  const getImageStatus = (image) => {
    if (image.status === 'pending') return 'pending';
    if (image.status === 'rejected') return 'rejected';
    return 'approved';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'rejected': return '❌';
      case 'approved': return '✅';
      default: return '❓';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'รออนุมัติ';
      case 'rejected': return 'ถูกปฏิเสธ';
      case 'approved': return 'อนุมัติแล้ว';
      default: return 'ไม่ทราบสถานะ';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'approved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  const handleUploadSuccess = (uploadedImages) => {
    onImageUpdate && onImageUpdate();
    setShowUploadModal(false);
  };

  // ตรวจสอบรูปซ้ำ
  const checkDuplicates = async () => {
    if (!girlId) return;
    
    try {
      const result = await checkDuplicatesForUser(girlId);
      setDuplicateCount(result.duplicateCount);
      
      if (result.duplicateCount > 0) {
        console.log('Found duplicate images:', result.duplicates);
      }
    } catch (error) {
      console.error('Error checking duplicates:', error);
    }
  };

  // ทำความสะอาดรูปซ้ำ
  const cleanupDuplicates = async () => {
    if (!girlId) return;
    
    const confirmed = window.confirm(
      `พบรูปภาพซ้ำ ${duplicateCount} รูป ต้องการลบออกหรือไม่?`
    );
    
    if (!confirmed) return;
    
    try {
      const result = await cleanupDuplicatesForUser(girlId);
      if (result.cleanedCount > 0) {
        setDuplicateCount(0);
        onImageUpdate && onImageUpdate();
        alert(`ทำความสะอาดรูปภาพซ้ำเรียบร้อยแล้ว ลบออก ${result.cleanedCount} รูป`);
      } else {
        alert('ไม่พบรูปซ้ำที่ต้องทำความสะอาด');
      }
    } catch (error) {
      console.error('Error cleaning up duplicates:', error);
      alert('เกิดข้อผิดพลาดในการทำความสะอาด');
    }
  };

  // ทำความสะอาดฐานข้อมูลโดยตรง
  const cleanupDatabase = async () => {
    if (!girlId) return;
    
    const confirmed = window.confirm(
      'ต้องการทำความสะอาดฐานข้อมูลโดยตรงหรือไม่? (จะลบรูปซ้ำที่ซ้ำกับรูปที่อนุมัติแล้ว)'
    );
    
    if (!confirmed) return;
    
    try {
      const result = await cleanupDatabaseDuplicates(girlId);
      if (result.cleanedCount > 0) {
        setDuplicateCount(0);
        onImageUpdate && onImageUpdate();
        alert(`ทำความสะอาดฐานข้อมูลเรียบร้อยแล้ว ลบออก ${result.cleanedCount} รูป`);
      } else {
        alert('ไม่พบรูปซ้ำที่ต้องทำความสะอาดในฐานข้อมูล');
      }
    } catch (error) {
      console.error('Error cleaning database:', error);
      alert('เกิดข้อผิดพลาดในการทำความสะอาดฐานข้อมูล');
    }
  };

  // ตรวจสอบรูปซ้ำเมื่อ component โหลด
  React.useEffect(() => {
    if (girlId) {
      checkDuplicates();
    }
  }, [girlId, images, pendingImages, rejectedImages]);

  // ลบรูปภาพ
  const handleDeleteImage = async (image, status) => {
    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะลบรูปภาพนี้?')) return;

    try {
      const girlRef = doc(db, 'girls', girlId);
      
      if (status === 'approved') {
        await updateDoc(girlRef, {
          images: arrayRemove(image),
          lastUpdated: new Date().toISOString()
        });
      } else if (status === 'pending') {
        await updateDoc(girlRef, {
          pendingImages: arrayRemove(image),
          lastUpdated: new Date().toISOString()
        });
      } else if (status === 'rejected') {
        await updateDoc(girlRef, {
          rejectedImages: arrayRemove(image),
          lastUpdated: new Date().toISOString()
        });
      }

      alert('ลบรูปภาพสำเร็จ!');
      onImageUpdate && onImageUpdate();
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('เกิดข้อผิดพลาดในการลบรูปภาพ');
    }
  };

  // อัพโหลดรูปใหม่ (สำหรับรูปที่ถูกปฏิเสธ)
  const handleReuploadImage = async (image) => {
    if (!window.confirm('คุณต้องการอัพโหลดรูปภาพนี้ใหม่หรือไม่?')) return;

    try {
      const girlRef = doc(db, 'girls', girlId);
      
      // ลบจาก rejectedImages และเพิ่มเข้า pendingImages
      await updateDoc(girlRef, {
        rejectedImages: arrayRemove(image),
        pendingImages: arrayUnion({
          ...image,
          status: 'pending',
          reuploadedAt: new Date().toISOString()
        }),
        lastUpdated: new Date().toISOString()
      });

      alert('อัพโหลดรูปภาพใหม่สำเร็จ! รอการอนุมัติจากแอดมิน');
      onImageUpdate && onImageUpdate();
    } catch (error) {
      console.error('Error reuploading image:', error);
      alert('เกิดข้อผิดพลาดในการอัพโหลดรูปภาพใหม่');
    }
  };


  // ตรวจสอบให้แน่ใจว่าเป็น array
  const safeImages = Array.isArray(images) ? images : [];
  const safePendingImages = Array.isArray(pendingImages) ? pendingImages : [];
  const safeRejectedImages = Array.isArray(rejectedImages) ? rejectedImages : [];

  const allImages = [
    ...safeImages.map(img => ({ ...img, status: 'approved' })),
    ...safePendingImages,
    ...safeRejectedImages
  ];

  // กรองรูปภาพตามสถานะ
  const getFilteredImages = () => {
    switch (activeImageTab) {
      case 'approved':
        return safeImages.map(img => ({ ...img, status: 'approved' }));
      case 'pending':
        return safePendingImages;
      case 'rejected':
        return safeRejectedImages;
      default:
        return allImages;
    }
  };

  const filteredImages = getFilteredImages();

  // Debug: ตรวจสอบค่าต่างๆ
  console.log('ImageGallery Debug:');
  console.log('- safeImages:', safeImages);
  console.log('- safePendingImages:', safePendingImages);
  console.log('- safeRejectedImages:', safeRejectedImages);
  console.log('- allImages:', allImages);
  console.log('- maxImages:', maxImages);
  console.log('- allImages.length:', allImages.length);
  console.log('- Button disabled:', maxImages !== -1 && allImages.length >= maxImages);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">รูปภาพ ({safeImages.length}/{maxImages === -1 ? '∞' : maxImages})</h3>
        {isOwner && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowUploadModal(true)}
              disabled={maxImages !== -1 && allImages.length >= maxImages}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              + เพิ่มรูป
            </button>
            
            {/* Debug button - แสดงเสมอ */}
            <button
              onClick={() => {
                console.log('🔍 Debug Info:');
                console.log('- Current tier:', currentTier);
                console.log('- Max images allowed:', maxImages);
                console.log('- Current images count:', allImages.length);
                console.log('- Can upload more:', maxImages === -1 || allImages.length < maxImages);
                alert(`Debug Info:\nSubscription Plan: ${currentTier}\nMax Images: ${maxImages}\nCurrent: ${allImages.length}\nCan Upload: ${maxImages === -1 || allImages.length < maxImages}`);
              }}
              className="px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm"
            >
              🔍 Debug
            </button>
          </div>
        )}
      </div>

      {/* Image Status Tabs */}
      {isOwner && (
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveImageTab('all')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
              activeImageTab === 'all'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            ทั้งหมด ({allImages.length})
          </button>
          <button
            onClick={() => setActiveImageTab('approved')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
              activeImageTab === 'approved'
                ? 'bg-white text-green-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            ✅ อนุมัติแล้ว ({safeImages.length})
          </button>
          <button
            onClick={() => setActiveImageTab('pending')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
              activeImageTab === 'pending'
                ? 'bg-white text-yellow-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            ⏳ รออนุมัติ ({safePendingImages.length})
          </button>
          <button
            onClick={() => setActiveImageTab('rejected')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
              activeImageTab === 'rejected'
                ? 'bg-white text-red-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            ❌ ถูกปฏิเสธ ({safeRejectedImages.length})
          </button>
        </div>
      )}

      {/* Image Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {filteredImages.length > 0 ? (
          filteredImages.map((image, index) => {
          const status = getImageStatus(image);
          return (
            <div
              key={index}
              className="relative group cursor-pointer"
              onClick={() => handleImageClick(image)}
            >
              <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={image.url}
                  alt={`Image ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDMwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRkZCNkMxIi8+Cjx0ZXh0IHg9IjE1MCIgeT0iMjAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiMzMzMiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkltYWdlIEVycm9yPC90ZXh0Pgo8L3N2Zz4K';
                  }}
                />
              </div>
              
              {/* Status Badge */}
              <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                <span className="mr-1">{getStatusIcon(status)}</span>
                {getStatusText(status)}
              </div>

              {/* Profile Image Badge */}
              {isOwner && girlData && isProfileImage(girlData, image.url) && (
                <div className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium bg-blue-500 text-white">
                  ⭐ รูปหลัก
                </div>
              )}

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-2">
                  <span className="text-white text-2xl">👁️</span>
                  {isOwner && (
                    <>
                      {status === 'rejected' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReuploadImage(image);
                          }}
                          className="text-white text-xl hover:text-blue-300 transition"
                          title="อัพโหลดใหม่"
                        >
                          🔄
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteImage(image, status);
                        }}
                        className="text-white text-xl hover:text-red-300 transition"
                        title="ลบรูปภาพ"
                      >
                        🗑️
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })
        ) : (
          // แสดง placeholder image เมื่อไม่มีรูป
          <div className="col-span-full flex justify-center">
            <div className="text-center">
              <img
                src={createImagePlaceholder('ยังไม่มีรูปภาพ', '📷')}
                alt="ไม่มีรูปภาพ"
                className="w-64 h-64 mx-auto rounded-lg"
              />
              <p className="mt-4 text-gray-500">
                {activeImageTab === 'all' && 'ยังไม่มีรูปภาพในระบบ'}
                {activeImageTab === 'approved' && 'ยังไม่มีรูปภาพที่อนุมัติแล้ว'}
                {activeImageTab === 'pending' && 'ยังไม่มีรูปภาพรออนุมัติ'}
                {activeImageTab === 'rejected' && 'ยังไม่มีรูปภาพที่ถูกปฏิเสธ'}
              </p>
              {isOwner && (
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  + เพิ่มรูปภาพแรก
                </button>
              )}
            </div>
          </div>
        )}

        {/* Empty Slots */}
        {Array.from({ length: maxImages - allImages.length }).map((_, index) => (
          <div
            key={`empty-${index}`}
            className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50"
          >
            <div className="text-center text-gray-400">
              <div className="text-2xl mb-1">📷</div>
              <div className="text-xs">ว่าง</div>
            </div>
          </div>
        ))}
      </div>

      {/* Upload Modal */}
      <ImageUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        girlId={girlId}
        currentImages={safeImages}
        maxImages={maxImages}
        onUploadSuccess={handleUploadSuccess}
        pendingImages={safePendingImages}
        rejectedImages={safeRejectedImages}
      />

      {/* Image Preview Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">ดูรูปภาพ</h3>
              <button
                onClick={() => setShowImageModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <img
                src={selectedImage.url}
                alt="Preview"
                className="w-full max-h-96 object-contain rounded-lg"
              />
              
              <div className="space-y-2">
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(getImageStatus(selectedImage))}`}>
                  <span className="mr-1">{getStatusIcon(getImageStatus(selectedImage))}</span>
                  {getStatusText(getImageStatus(selectedImage))}
                </div>
                
                {selectedImage.originalName && (
                  <p className="text-sm text-gray-600">
                    <strong>ชื่อไฟล์:</strong> {selectedImage.originalName}
                  </p>
                )}
                
                {selectedImage.uploadedAt && (
                  <p className="text-sm text-gray-600">
                    <strong>อัพโหลดเมื่อ:</strong> {new Date(selectedImage.uploadedAt).toLocaleString('th-TH')}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowImageModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  ปิด
                </button>
                {isOwner && (
                  <>
                    {getImageStatus(selectedImage) === 'rejected' && (
                      <button
                        onClick={() => {
                          handleReuploadImage(selectedImage);
                          setShowImageModal(false);
                        }}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                      >
                        🔄 อัพโหลดใหม่
                      </button>
                    )}
                    <button
                      onClick={() => {
                        handleDeleteImage(selectedImage, getImageStatus(selectedImage));
                        setShowImageModal(false);
                      }}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                    >
                      🗑️ ลบรูปภาพ
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Image Management */}
      {isOwner && girlData && (
        <div className="mb-6">
          <ProfileImageSelector 
            girlData={girlData}
            onProfileImageUpdate={onImageUpdate}
            isOwner={isOwner}
          />
        </div>
      )}

      {/* Tier Information */}
      {isOwner && (
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            {(() => {
              const ranking = getSubscriptionRanking('girl', currentTier);
              return (
                <>
                  <span className="text-lg">{ranking.icon}</span>
                  <h4 className="font-semibold text-blue-800">ข้อมูล Subscription Plan</h4>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Rank {ranking.rank}
                  </span>
                </>
              );
            })()}
          </div>
          <div className="text-sm text-blue-700 space-y-1">
            <p>• สามารถอัพโหลดได้สูงสุด {maxImages === -1 ? 'ไม่จำกัด' : `${maxImages} รูป`}</p>
            <p>• รูปที่อัพโหลดแล้ว: {safeImages.length} รูป</p>
            <p>• รูปรออนุมัติ: {safePendingImages.length} รูป</p>
            <p>• รูปที่ถูกปฏิเสธ: {safeRejectedImages.length} รูป</p>
            {duplicateCount > 0 && (
              <p className="text-red-600 font-medium">• ⚠️ พบรูปซ้ำ: {duplicateCount} รูป</p>
            )}
          </div>
          
          {duplicateCount > 0 && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium mb-2">
                ⚠️ พบรูปภาพซ้ำ {duplicateCount} รูป
              </p>
              <p className="text-xs text-red-700 mb-2">
                รูปซ้ำอาจทำให้เกิน tier limit และสร้างความสับสน
              </p>
              <div className="flex gap-2">
                <button 
                  onClick={cleanupDuplicates}
                  className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                >
                  🧹 ทำความสะอาดรูปซ้ำ
                </button>
                <button 
                  onClick={cleanupDatabase}
                  className="px-3 py-1 bg-orange-500 text-white text-xs rounded hover:bg-orange-600"
                >
                  🗄️ ทำความสะอาดฐานข้อมูล
                </button>
              </div>
            </div>
          )}
          
          {maxImages !== -1 && allImages.length >= maxImages && maxImages > 0 && (
            <div className="mt-3 p-3 bg-yellow-100 border border-yellow-300 rounded-lg">
              <p className="text-sm text-yellow-800 font-medium mb-2">
                ⚠️ คุณอัพโหลดรูปครบจำนวนที่กำหนดแล้ว
              </p>
              <p className="text-xs text-yellow-700 mb-2">
                อัพเกรด Subscription Plan เพื่ออัพโหลดรูปเพิ่มเติม:
              </p>
              <div className="text-xs text-yellow-700 space-y-1">
                <p>• <strong>Basic:</strong> 3 รูป (100 T Coins/เดือน)</p>
                <p>• <strong>Premium:</strong> 6 รูป (300 T Coins/เดือน)</p>
                <p>• <strong>VIP:</strong> ไม่จำกัด (500 T Coins/เดือน)</p>
              </div>
              <button 
                onClick={() => setShowTierUpgrade(true)}
                className="mt-2 px-3 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700"
              >
                อัพเกรด Subscription Plan
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tier Upgrade Modal */}
      <TierUpgradeModal
        isOpen={showTierUpgrade}
        onClose={() => setShowTierUpgrade(false)}
        currentTier={currentTier}
        userRole={userRole}
        userId={girlId}
        onUpgradeSuccess={() => {
          onImageUpdate && onImageUpdate();
          setShowTierUpgrade(false);
          // รีเฟรชข้อมูล Subscription Plan
          window.location.reload();
        }}
      />

    </div>
  );
};

export default ImageGallery;
